import { RotateCwIcon } from "lucide-react";

import { cn } from "~/lib/utils";

import type { UnreadableThreadPresentation } from "./ThreadUnreadableState.logic";
import { Button } from "./ui/button";
import { Skeleton } from "./ui/skeleton";
import {
  WorkspaceEmptyStateFrame,
  WorkspaceEmptyStateInset,
  WorkspaceEmptyStateMessage,
} from "./WorkspaceEmptyStateFrame";

export interface ThreadUnreadableStateProps {
  readonly presentation: UnreadableThreadPresentation;
  readonly onRetry: () => void;
}

/**
 * Stands in for a selected thread the client cannot read while its Environment is
 * reachable: a history window that failed to load, a thread still synchronizing, or one
 * the Environment has withdrawn. The no-thread-selected empty state must never answer
 * for any of those, and the retry here is the user's way back into the thread.
 */
export function ThreadUnreadableStateView({ presentation, onRetry }: ThreadUnreadableStateProps) {
  if (presentation.loading) {
    return (
      <WorkspaceEmptyStateFrame heading={presentation.heading} layout="timeline">
        <ThreadConversationGhost label={presentation.detail} />
        {presentation.retryLabel === null ? null : (
          <div className="mt-8 flex justify-center">
            <Button type="button" variant="ghost" size="xs" onClick={onRetry}>
              <RotateCwIcon className="size-3" aria-hidden />
              {presentation.retryLabel}
            </Button>
          </div>
        )}
      </WorkspaceEmptyStateFrame>
    );
  }
  return (
    <WorkspaceEmptyStateFrame heading={presentation.heading}>
      <WorkspaceEmptyStateMessage
        {...(presentation.title === null ? {} : { title: presentation.title })}
        detail={presentation.detail}
        {...(presentation.retryLabel === null
          ? {}
          : {
              action: (
                <Button type="button" variant="outline" size="sm" onClick={onRetry}>
                  <RotateCwIcon className="size-3.5" aria-hidden />
                  {presentation.retryLabel}
                </Button>
              ),
            })}
      />
    </WorkspaceEmptyStateFrame>
  );
}

export function ThreadUnreadableState(props: ThreadUnreadableStateProps) {
  return (
    <WorkspaceEmptyStateInset>
      <ThreadUnreadableStateView {...props} />
    </WorkspaceEmptyStateInset>
  );
}

/** Widths cycle rather than randomize, so the ghost renders the same on every pass. */
const GHOST_TURNS = [
  { key: "first", user: "w-2/5", reply: ["w-full", "w-11/12", "w-4/5"] },
  { key: "second", user: "w-1/3", reply: ["w-full", "w-3/4"] },
] as const;

/**
 * The conversation's own shape while its history has not arrived: a user message over an
 * agent reply, twice, in the timeline's column. Standing in for the timeline keeps the
 * loaded thread from replacing one shape with another a moment later.
 */
function ThreadConversationGhost({ label }: { readonly label: string }) {
  return (
    <div
      role="status"
      aria-label={label}
      className="mx-auto flex w-full max-w-3xl flex-col gap-8 motion-safe:animate-skeleton"
    >
      {GHOST_TURNS.map((turn) => (
        <div key={turn.key} className="flex flex-col gap-4">
          <Skeleton className={cn("ml-auto h-10 rounded-2xl", turn.user)} />
          <div className="flex flex-col gap-2">
            {turn.reply.map((width) => (
              <Skeleton key={width} className={cn("h-3 rounded-full", width)} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
